import { getDb, STORE_PENDING_JORNADAS } from './db';
import { claveDeMercado } from '../domain/levantamiento';

/**
 * Cola local del cierre de jornada del levantamiento.
 *
 * POR QUÉ TAMBIÉN ESTE tiene cola: el cierre se llena **al terminar el día en ese mercado**, o
 * sea todavía adentro, donde no hay cobertura. Sin cola, las cinco respuestas que explican el
 * día —afluencia, feria, lluvia, paro, poca pesca— se perderían justo en los días en que más
 * explican algo.
 *
 * Es más simple que la de visitas: no lleva fotos, así que no hay nada que subir antes.
 */

export interface JornadaEnCola {
  clientUuid: string;
  campaignId: string;
  pdvId: string;
  mercadoLibre: string;
  fecha: string;
  /** Quién cierra: con cuentas genéricas compartidas, `worker_id` no dice quién fue. */
  auditorDeclarado: string | null;
  afluencia: 'alta' | 'normal' | 'baja';
  /** «¿Hoy era día con alguna actividad adicional?» — texto exacto del Excel. */
  actividadAdicional: boolean;
  factorExterno: string;
  competenciaActiva: string | null;
  observacion: string | null;
}

export interface QueuedJornada {
  clientUuid: string;
  input: JornadaEnCola;
  attempts: number;
  lastError: string | null;
  status: 'pendiente' | 'fallido';
}

export const MAX_JORNADA_ATTEMPTS = 5;

export interface JornadaUploader {
  registrarJornada(input: JornadaEnCola): Promise<string>;
  isPermanentError(err: unknown): boolean;
}

let uploader: JornadaUploader | null = null;

export function setJornadaUploader(u: JornadaUploader): void {
  uploader = u;
}

/**
 * Guarda el cierre en la cola. Idempotente por `clientUuid`, y además el servidor lo resuelve
 * por (campaña, mercado, persona, fecha): volver a mandarlo REEMPLAZA el anterior en vez de
 * acumular dos cierres que se contradicen.
 */
export async function enqueueJornada(input: JornadaEnCola): Promise<void> {
  const db = await getDb();
  const existing = await db.get(STORE_PENDING_JORNADAS, input.clientUuid);
  await db.put(STORE_PENDING_JORNADAS, {
    clientUuid: input.clientUuid,
    input,
    attempts: existing?.attempts ?? 0,
    lastError: existing?.lastError ?? null,
    status: existing?.status ?? 'pendiente',
  });
}

export async function listPendingJornadas(): Promise<QueuedJornada[]> {
  const db = await getDb();
  return (await db.getAll(STORE_PENDING_JORNADAS)).filter((i) => i.status !== 'fallido');
}

export async function listFailedJornadas(): Promise<QueuedJornada[]> {
  const db = await getDb();
  return (await db.getAll(STORE_PENDING_JORNADAS)).filter((i) => i.status === 'fallido');
}

export async function pendingJornadaCount(): Promise<number> {
  return (await listPendingJornadas()).length;
}

/** El cierre ya guardado para ese mercado y día, si todavía está en la cola local. */
export async function cierreLocalDe(
  clave: string, fecha: string,
): Promise<JornadaEnCola | null> {
  const db = await getDb();
  const todas = await db.getAll(STORE_PENDING_JORNADAS);
  return todas.find(
    (j) => claveDeMercado(j.input.pdvId, j.input.mercadoLibre ?? '') === clave
      && j.input.fecha === fecha,
  )?.input ?? null;
}

function errorMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/** Sube los cierres pendientes. Mismas reglas que las otras colas: la red no gasta intentos. */
export async function flushJornadas(): Promise<{ sent: number; remaining: number }> {
  const db = await getDb();
  const items = (await db.getAll(STORE_PENDING_JORNADAS)).filter((i) => i.status !== 'fallido');
  if (items.length === 0) return { sent: 0, remaining: 0 };
  if (!uploader) {
    throw new Error('La cola de cierres no tiene enviador registrado (importa api/levantamiento).');
  }

  let sent = 0;
  for (const item of items) {
    try {
      await uploader.registrarJornada({ ...item.input, clientUuid: item.clientUuid });
      await db.delete(STORE_PENDING_JORNADAS, item.clientUuid);
      sent += 1;
    } catch (err) {
      const permanent = uploader.isPermanentError(err);
      const attempts = permanent ? item.attempts + 1 : 0;
      await db.put(STORE_PENDING_JORNADAS, {
        ...item,
        attempts,
        lastError: errorMessage(err),
        status: permanent && attempts >= MAX_JORNADA_ATTEMPTS ? 'fallido' : 'pendiente',
      });
    }
  }

  const remaining = (await listPendingJornadas()).length;
  return { sent, remaining };
}
